import { useState } from "react"
import { Clock, Search, Check } from "lucide-react"
import { cn } from "../lib/util"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"

interface CustomTimePickerProps {
  selectedTime: string
  onTimeSelect: (time: string) => void
  isLoading?: boolean
  isDisabled?: boolean
  className?: string
  error?: boolean | string | undefined;
  availableTimes?: string[]
}

export function CustomTimePicker({
  selectedTime,
  onTimeSelect,
  isLoading = false,
  isDisabled = false,
  className,
  error = false,
  availableTimes,
}: CustomTimePickerProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  // Convert 24-hour time to 12-hour format
  const convertTo12Hour = (time24: string) => {
    const [hour24, minute] = time24.split(':').map(Number)
    const period = hour24 >= 12 ? 'PM' : 'AM'
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24
    return `${hour12}:${minute.toString().padStart(2, '0')} ${period}`
  }

  // Convert 12-hour time to 24-hour format
  const convertTo24Hour = (time12: string) => {
    const [time, period] = time12.split(' ')
    const [hour, minute] = time.split(':').map(Number)
    let hour24 = hour
    if (period === 'PM' && hour !== 12) {
      hour24 = hour + 12
    } else if (period === 'AM' && hour === 12) {
      hour24 = 0
    }
    return `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
  }

  // Generate time slots starting from 12:00 PM to 11:30 AM (PM first, then AM)
  const generateTimeSlots = () => {
    const slots = []
    // PM times: 12:00 PM to 11:30 PM
    for (let hour = 12; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const h = hour.toString().padStart(2, "0")
        const m = minute.toString().padStart(2, "0")
        slots.push(`${h}:${m}`)
      }
    }
    // AM times: 12:00 AM to 11:30 AM
    for (let hour = 0; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const h = hour.toString().padStart(2, "0")
        const m = minute.toString().padStart(2, "0")
        slots.push(`${h}:${m}`)
      }
    }
    return slots
  }

  const allTimeSlots = availableTimes || generateTimeSlots()

  // Convert time slots to 12-hour format for display
  const displayTimeSlots = allTimeSlots.map(time => ({
    value24h: time,
    display: convertTo12Hour(time)
  }))

  // Filter time slots based on search query (search in 12-hour format)
  const timeSlots = searchQuery
    ? displayTimeSlots.filter((slot) => slot.display.toLowerCase().includes(searchQuery.toLowerCase()))
    : displayTimeSlots

  // Handle time selection
  const handleTimeSelect = (time24h: string) => {
    onTimeSelect(time24h)
    setOpen(false)
  }

  // Get display value for selected time
  const displayValue = selectedTime ? convertTo12Hour(selectedTime) : null

  return (
    <div className={cn("w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={isDisabled || isLoading}
            className={cn(
              "flex min-h-[48px] w-full items-center justify-between rounded-xl border bg-white px-4 py-3 text-left text-base transition-colors",
              "outline-none focus-visible:border-[#B87775] focus-visible:ring-2 focus-visible:ring-[#B87775]/50",
              selectedTime ? "text-[#3a2e2c]" : "text-[#bcaca9]",
              error ? "border-[#d98c89]" : "border-[#E3C9C8] hover:border-[#B87775]",
              (isDisabled || isLoading) && "cursor-not-allowed opacity-60"
            )}
          >
            {displayValue ? displayValue : <span>Select a time</span>}
            <Clock className="h-4 w-4 shrink-0 text-[#B87775]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[220px] overflow-hidden rounded-xl border border-[#EAD7D6] p-0 shadow-lg"
          align="start"
        >
          <div className="flex flex-col">
            {/* Search input */}
            <div className="flex items-center gap-2 border-b border-[#F0E2E1] px-3 py-2.5">
              <Search className="h-4 w-4 shrink-0 text-[#B87775]" />
              <input
                placeholder="Search time…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-7 w-full border-0 bg-transparent p-0 text-sm text-[#3a2e2c] outline-none placeholder:text-[#bcaca9]"
              />
            </div>

            {/* Time list */}
            <div className="max-h-[280px] overflow-y-auto">
              {timeSlots.length === 0 ? (
                <div className="py-6 text-center text-sm text-[#9A6B69]">No time found.</div>
              ) : (
                <div className="p-1">
                  {timeSlots.map((slot) => {
                    const active = selectedTime === slot.value24h
                    return (
                      <button
                        key={slot.value24h}
                        type="button"
                        onClick={() => handleTimeSelect(slot.value24h)}
                        className={cn(
                          "flex w-full cursor-pointer select-none items-center justify-between rounded-lg px-3 py-2 text-sm tabular-nums outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#B87775]",
                          active
                            ? "bg-[#FBEFEF] font-medium text-[#B87775]"
                            : "text-[#3a2e2c] hover:bg-[#FBEFEF]"
                        )}
                      >
                        {slot.display}
                        {active && <Check className="h-4 w-4 text-[#B87775]" />}
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {isLoading && (
        <div className="mt-2 flex items-center gap-2 text-sm text-[#9A6B69]">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#EAD7D6] border-t-[#B87775]"></div>
          Checking availability…
        </div>
      )}
    </div>
  )
}
